/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2023
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Checkbox, Icon, Input, Pagination, Table } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import _ from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import descriptors from '../../../locales/en-US/descriptors';
import withLoadingStatus from '../../shared/StatusWrappers/LoadingStatus';

import {
  IAssociationDataValidations,
  IElementDataValidations,
  IPropertyValidation,
  IValidationDetails,
  compareAfterLowerCaseNoSpaces
} from '../../shared/Utils/EquipmentSpaceMappingAndProperties/DataUtils/DataValidator';
import { IValidationMessageDescriptor } from '../../shared/Utils/EquipmentSpaceMappingAndProperties/FileReaderUtils/FileValidator';
import {
  ZLOEquipments,
  ZLOElementProperties
} from '../ZLODataUtils/DataConverter';

import './PreviewTableTheme.scss';

type IAny = any;

const InfoIcon = () => <Icon size="small" name="badge-info" root="common" />;
const zloDescriptors = descriptors.ZLOOnboardingConfig;
const eqspmd = descriptors.equipmentSpaceMapping;

interface IZLOValidatorCellProps {
  value: IAny;
  isValid: boolean;
  messages: IValidationDetails[];
}

const ZLOValidatorCell: FC<IZLOValidatorCellProps> = (
  props: IZLOValidatorCellProps
) => {
  const { value, isValid, messages } = props;
  const [t] = useTranslation();
  const _getValidationMessage = (m: IValidationMessageDescriptor) => {
    if (m.args) return t(m.d, m.args);
    return t(m.d);
  };
  return (
    <div>
      <div className="text-content">{value}</div>
      {_.map(messages, (m, i) => (
        <div
          key={`val-message-${i}`}
          className={`cell-validation-message-wrapper ${
            m.type !== 'info' ? 'invalid' : 'valid'
          }`}
        >
          <span
            className={`icon-container ${
              m.type !== 'info' ? 'error-icon' : 'info-icon'
            }`}
          >
            <InfoIcon />
          </span>
          <span>{_getValidationMessage(m)}</span>
        </div>
      ))}
    </div>
  );
};

interface IZLOTableRowProps {
  index: number;
  rowData: IAssociationDataValidations;
  errorMaps: {};
}

const ZLOTableRow: FC<IZLOTableRowProps> = (props: IZLOTableRowProps) => {
  const { rowData, index } = props;
  const [t] = useTranslation();

  const renderProperty = (val?: IPropertyValidation) => {
    return val ? (
      <ZLOValidatorCell
        value={_.capitalize(_.toString(val.propertyValue))}
        isValid={val.isValid}
        messages={val.validationDetails}
      />
    ) : (
      <ZLOValidatorCell value={'-'} isValid={true} messages={[]} />
    );
  };

  const { _rowData, _rowSpan } = useMemo(() => {
    const vavs = _.filter(
      rowData.validatedAssociations,
      (a) =>
        compareAfterLowerCaseNoSpaces(a.ElementType, ZLOEquipments.VAV) ||
        compareAfterLowerCaseNoSpaces(a.ElementType, ZLOEquipments.VAV2)
    );
    const oss = _.filter(rowData.validatedAssociations, (a) =>
      compareAfterLowerCaseNoSpaces(a.ElementType, ZLOEquipments.OS)
    );
    const _rowSpan = vavs.length || 1;
    const _rd: any[][] = Array.from({ length: _rowSpan }, () =>
      Array.from({ length: 8 }, () => '')
    );

    //set first row
    _rd[0][0] = <div className="text-content">{index}</div>;
    _rd[0][1] = (
      <ZLOValidatorCell
        value={rowData.SpatialElementName}
        isValid={rowData.isValid}
        messages={rowData.validationDetails}
      />
    );
    _rd[0][2] = (
      <div className="text-content">
        {vavs.length > 0 ? t(zloDescriptors.VAV_ETYPE) : t(eqspmd.EMPTY)}
      </div>
    );
    _.forEach(vavs, (v, i) => {
      _rd[i][3] = (
        <ZLOValidatorCell
          value={v.escapedElementName}
          isValid={v.isValid}
          messages={v.validationDetails}
        />
      );
      const zloState = _.find(
        v.validatedProperties,
        (p) => p.propertyKey === ZLOElementProperties.ZLOState
      );
      const zoneOptimSetback = _.find(
        v.validatedProperties,
        (p) => p.propertyKey === ZLOElementProperties.ZoneOptimizationSetback
      );
      _rd[i][4] = renderProperty(zloState);
      _rd[i][5] = renderProperty(zoneOptimSetback);
    });
    _rd[0][6] = (
      <div className="text-content">
        {oss.length > 0
          ? t(zloDescriptors.OCCUPANCY_SENSOR_ETYPE)
          : t(eqspmd.EMPTY)}
      </div>
    );
    _rd[0][7] = (
      <>
        {_.map(oss, (v, i) => (
          <>
            {i > 0 && <hr className="divider" />}
            <div key={`os-${i}`} className="div-inside-td">
              <ZLOValidatorCell
                value={v.ElementName}
                isValid={v.isValid}
                messages={v.validationDetails}
              />
            </div>
          </>
        ))}
      </>
    );
    // _.forEach(oss, (v, i) => {
    //   _rd[i][7] = (
    //     <ZLOValidatorCell
    //       value={v.ElementName}
    //       isValid={v.isValid}
    //       messages={v.validationDetails}
    //     />
    //   );
    // });
    return { _rowData: _rd, _rowSpan, vavs, oss };
  }, [rowData.SpatialElementName]);

  return (
    <>
      {_.map(_rowData, (rd, i) => (
        <tr key={`r-${i}-${index}`} className="zlo-table-row">
          {rd[0] && (
            <td className="zlo-table-row-cell" rowSpan={_rowSpan}>
              {rd[0]}
            </td>
          )}
          {rd[1] && (
            <td className="zlo-table-row-cell" rowSpan={_rowSpan}>
              {rd[1]}
            </td>
          )}
          {rd[2] && (
            <td className="zlo-table-row-cell" rowSpan={_rowSpan}>
              {rd[2]}
            </td>
          )}
          <td className="zlo-table-row-cell">{rd[3]}</td>
          <td className="zlo-table-row-cell">{rd[4]}</td>
          <td className="zlo-table-row-cell">{rd[5]}</td>
          {rd[6] && (
            <td className="zlo-table-row-cell" rowSpan={_rowSpan}>
              {rd[6]}
            </td>
          )}
          {rd[7] && (
            <td
              className="mutliple zlo-table-row-cell has-table"
              rowSpan={_rowSpan}
            >
              {rd[7]}
            </td>
          )}
        </tr>
      ))}
    </>
  );
};

export interface IZLOOnboardingPreviewTableProps {
  validatedData: IAssociationDataValidations[];
  numErrors: number;
  pagination?: boolean;
}

interface IIndexedAssocDataValidations extends IAssociationDataValidations {
  index: number;
}

export const ZLOOnboardingPreviewTable: FC<IZLOOnboardingPreviewTableProps> = (
  props: IZLOOnboardingPreviewTableProps
) => {
  const {
    validatedData,
    pagination: paginationEnabled,
    numErrors: totalErrors
  } = props;
  const [t] = useTranslation();
  const [showAll, setShowAll] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');

  const [currPage, setCurrPage] = useState<number>(1); // pages start from 1
  const [entriesPerPage, setEntriesPerPage] = useState<number>(5);
  const minSearchLen = 2;

  const data: IIndexedAssocDataValidations[] = validatedData.map(
    (d, index) => ({
      index: index + 1,
      ...d
    })
  );

  const { filteredData, invalidDataLength } = useMemo(() => {
    let _filteredData = data;
    const invalidDataLength = data.filter((d) => !d.allValid).length;
    if (!_.isEmpty(searchString) && searchString.length >= minSearchLen) {
      let _searchStr = _.toLower(searchString);
      _filteredData = _.filter(data, (r) => {
        let toShow = false;
        const { SpatialElementName, validatedAssociations } = r;
        toShow = toShow || _.toLower(SpatialElementName).includes(_searchStr);

        const checkLowerValid = (e: IAny) => {
          return (
            _.toLower(e.ElementName).includes(_searchStr) ||
            _.toLower(e.ElementType).includes(_searchStr)
          );
        };

        const checkSpacesAfterLower = (e: IAny) => {
          return (
            (compareAfterLowerCaseNoSpaces(e.ElementType, ZLOEquipments.VAV) ||
              compareAfterLowerCaseNoSpaces(
                e.ElementType,
                ZLOEquipments.VAV2
              )) &&
            _.toLower(
              _.join([ZLOEquipments.VAV, ZLOEquipments.VAV2], ', ')
            ).includes(_searchStr)
          );
        };

        const _assoc = _.filter(
          validatedAssociations,
          (e) =>
            checkLowerValid(e) ||
            checkSpacesAfterLower(e) ||
            (compareAfterLowerCaseNoSpaces(e.ElementType, ZLOEquipments.OS) &&
              _.toLower(ZLOEquipments.OS).includes(_searchStr))
        );
        toShow = toShow || _assoc.length > 0;

        const _props: { val: string }[] = [];
        _.forEach(validatedAssociations, (e) =>
          _.forEach(e.validatedProperties, (p) => {
            _props.push({ val: p.propertyValue });
          })
        );
        const _fprops = _.filter(_props, (p) =>
          _.toLower(p.val).includes(_searchStr)
        );
        toShow = toShow || _fprops.length > 0;

        return toShow;
      });
    }
    if (invalidDataLength > 0 && !showAll) {
      _filteredData = _.filter(_filteredData, (r) => !r.allValid);
    }

    return { filteredData: _filteredData, invalidDataLength };
  }, [data, searchString, showAll]);

  const { paginatedData } = useMemo(() => {
    let paginatedData = filteredData;
    if (paginationEnabled) {
      const _pageEnd = currPage * entriesPerPage;
      const _pageStartIndex = _pageEnd - entriesPerPage;
      const _pageLastIndex = _pageStartIndex + entriesPerPage;
      paginatedData = _.slice(filteredData, _pageStartIndex, _pageLastIndex);
    }
    return { paginatedData };
  }, [currPage, entriesPerPage, filteredData.length, showAll, searchString]);

  return (
    <>
      <div className="zlo-onboarding-validation-preview-wrapper">
        <>
          {invalidDataLength > 0 ? (
            <>
              <div className="preview-table-top-message">
                {t(zloDescriptors.FIX_VAL_ERRORS_MSG)}
              </div>
              <div className="invalid-items-alert-wrapper">
                <div className="icon-container error-icon">
                  <Icon name="badge-info" root="common" />
                </div>
                <div>
                  {t(zloDescriptors.ERROR_BOX_TEXT, {
                    numErrors: totalErrors
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="preview-table-top-message ok-msg">
              {t(zloDescriptors.VAL_OK_SUBMIT_MSG)}
            </div>
          )}
          <div className="validation-table-wrapper">
            <Table className="validation-preview-table">
              <thead className="validation-table-thead">
                <tr>
                  <th colSpan={8} className="search-table-header">
                    <div className="count-checkbox-wrapper">
                      <div className="count-wrapper">
                        <div>
                          <Input
                            id={'zlo-validation-preview-table-search'}
                            placeholder={t(zloDescriptors.SEARCH_PLACEHOLDER)}
                            max={64}
                            value={searchString}
                            onChange={(searchVal) => {
                              setSearchString(searchVal);
                            }}
                            icon={
                              <Icon
                                root="common"
                                name={
                                  searchString.length > 0 ? 'close' : 'search'
                                }
                                onClick={() => {
                                  if (searchString.length > 0)
                                    setSearchString('');
                                }}
                              />
                            }
                            error={
                              searchString.length > 0 &&
                              searchString.length < minSearchLen
                                ? t(zloDescriptors.SEARCH_MIN_LEN, {
                                    len: minSearchLen
                                  })
                                : undefined
                            }
                            reserveSpace={false}
                          />
                        </div>
                        {invalidDataLength > 0 && !showAll ? (
                          <>
                            <span>
                              {t(zloDescriptors.CURRENTLY_SHOWING, {
                                num: invalidDataLength,
                                total: data.length
                              })}
                            </span>
                          </>
                        ) : (
                          <>
                            <span>{data.length}</span>
                            <span>{t(zloDescriptors.TOTAL)}</span>
                          </>
                        )}
                      </div>
                      {invalidDataLength > 0 && (
                        <Checkbox
                          checked={showAll}
                          onChange={(checked) => {
                            setShowAll(checked);
                          }}
                          className="show-all-checkbox"
                          label={t(zloDescriptors.SHOW_ALL_ENTRIES)}
                        />
                      )}
                    </div>
                  </th>
                </tr>
                <tr className="table-header-row">
                  <th rowSpan={2} className="text-center">
                    {t(zloDescriptors.SL_NO)}
                  </th>
                  <th rowSpan={2} className="text-center">
                    {t(zloDescriptors.SPATIAL_ELE_NAME)}
                  </th>
                  <th colSpan={4} className="text-center">
                    {t(zloDescriptors.VAV)}
                  </th>
                  <th colSpan={2} className="text-center">
                    {t(zloDescriptors.OCCUPANCY_SENSOR)}
                  </th>
                </tr>
                <tr className="table-header-row">
                  <th>{t(zloDescriptors.ELE_TYPE)}</th>
                  <th>{t(zloDescriptors.ELE_NAME)}</th>
                  <th>{t(zloDescriptors.ZLO_STATE)}</th>
                  <th>{t(zloDescriptors.ZO_SETBACK)}</th>
                  <th>{t(zloDescriptors.ELE_TYPE)}</th>
                  <th>{t(zloDescriptors.ELE_NAME)}</th>
                </tr>
              </thead>
              <tbody className="validation-table-tbody">
                {paginatedData.length > 0 ? (
                  _.map(paginatedData, (r, i) => (
                    <ZLOTableRow
                      key={`key-${i + 1}`}
                      rowData={r}
                      index={r.index}
                      errorMaps={{}}
                    />
                  ))
                ) : (
                  <tr className="zlo-table-row">
                    <td className="zlo-table-row-cell text-center" colSpan={8}>
                      {t(zloDescriptors.NO_DATA)}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {paginationEnabled && (
            <div className="zlo-pagination-wrapper">
              <Pagination
                totalItems={filteredData.length}
                itemsPerPage={entriesPerPage}
                itemsPerPageOptions={[5, 10, 50, 75]}
                activePage={currPage}
                onPageChange={(page, _entriesPerPage) => {
                  setEntriesPerPage(_.toInteger(_entriesPerPage));
                  setCurrPage(_.toInteger(page));
                }}
                itemsPerPageAllEnabled={false}
                showDisplayDetails
              />
            </div>
          )}
        </>
      </div>
    </>
  );
};

export const ZLOOnboardingPreviewTableWithLoader = withLoadingStatus(
  ZLOOnboardingPreviewTable
);
